import type { AuditEntryDto, DepartmentDto, EmailLogDto, Page, SettingsDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Empty, formatDate, Pager, useToast } from '../lib/ui';
import { TripSettings } from './trip-settings';

const LIMIT = 50;

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Вход в систему',
  'auth.password_changed': 'Смена пароля',
  'auth.password_reset_requested': 'Запрос сброса пароля',
  'auth.password_reset': 'Пароль восстановлен',
  'user.created': 'Создан сотрудник',
  'user.updated': 'Изменён сотрудник',
  'user.password_reset_sent': 'Отправлена ссылка на пароль',
  'user.imported': 'Импорт из старой системы',
  'user.admin_bootstrap': 'Назначен администратор',
  'department.created': 'Создан отдел',
  'department.updated': 'Изменён отдел',
  'department.deleted': 'Удалён отдел',
  'notifications.broadcast': 'Рассылка уведомления',
};

function Changes({ before, after }: { before: unknown; after: unknown }) {
  if (!before && !after) return <span className="faint">—</span>;
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  const show = (v: unknown) => (v === null || v === undefined || v === '' ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  return (
    <span style={{ display: 'grid', gap: 2, fontSize: '0.87rem' }}>
      {keys.slice(0, 8).map((k) => (
        <span key={k}>
          <span className="faint">{k}: </span>
          {before ? (
            <>
              <s className="faint">{show(b[k])}</s> → {show(a[k])}
            </>
          ) : (
            show(a[k])
          )}
        </span>
      ))}
    </span>
  );
}

export function AuditPage() {
  const [offset, setOffset] = useState(0);
  const [entityType, setEntityType] = useState('');
  const log = useQuery({
    queryKey: ['audit', offset, entityType],
    queryFn: () => api.get<Page<AuditEntryDto>>('/audit', { limit: LIMIT, offset, entityType }),
    placeholderData: (p) => p,
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Журнал действий</h1>
          <p className="muted">Кто, что и когда изменил — с значениями «было — стало»</p>
        </div>
      </div>
      <div className="card">
        <div className="toolbar">
          <select className="select" value={entityType} onChange={(e) => { setEntityType(e.target.value); setOffset(0); }} aria-label="Раздел">
            <option value="">Все разделы</option>
            <option value="user">Сотрудники и вход</option>
            <option value="department">Отделы</option>
            <option value="notification">Рассылки</option>
          </select>
        </div>
        {log.data?.items.length === 0 && <Empty emoji="📜" title="Записей нет" />}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                <th className="hide-mobile">Изменения</th>
              </tr>
            </thead>
            <tbody>
              {log.data?.items.map((e) => (
                <tr key={e.id}>
                  <td className="faint" style={{ whiteSpace: 'nowrap' }}>{formatDate(e.createdAt)}</td>
                  <td>{e.actorName ?? <span className="faint">система</span>}</td>
                  <td>
                    {ACTION_LABELS[e.action] ?? e.action}
                    {e.ip && <div className="faint">{e.ip}</div>}
                  </td>
                  <td className="hide-mobile">
                    <Changes before={e.before} after={e.after} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {log.data && <Pager total={log.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>
    </>
  );
}

const STATUS: Record<EmailLogDto['status'], [string, string]> = {
  pending: ['В очереди', 'badge-warn'],
  sending: ['Отправляется', 'badge-warn'],
  sent: ['Отправлено', 'badge-brand'],
  failed: ['Ошибка', 'badge-danger'],
};

export function EmailLogPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('');
  const log = useQuery({
    queryKey: ['email-log', offset, status],
    queryFn: () => api.get<Page<EmailLogDto>>('/email-log', { limit: LIMIT, offset, status }),
    placeholderData: (p) => p,
    refetchInterval: 15_000,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/email-log/${id}/retry`),
    onSuccess: () => {
      toast('Письмо поставлено в очередь повторно');
      void qc.invalidateQueries({ queryKey: ['email-log'] });
    },
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Журнал писем</h1>
          <p className="muted">Письма уходят фоном; при ошибке система повторяет отправку несколько раз</p>
        </div>
      </div>
      <div className="card">
        <div className="toolbar">
          <select className="select" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} aria-label="Статус">
            <option value="">Все письма</option>
            <option value="failed">С ошибкой</option>
            <option value="pending">В очереди</option>
            <option value="sent">Отправленные</option>
          </select>
        </div>
        {log.data?.items.length === 0 && <Empty emoji="📭" title="Писем нет" />}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Создано</th>
                <th>Кому</th>
                <th>Тема</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {log.data?.items.map((m) => (
                <tr key={m.id}>
                  <td className="faint" style={{ whiteSpace: 'nowrap' }}>{formatDate(m.createdAt)}</td>
                  <td>{m.toAddress}</td>
                  <td>{m.subject}</td>
                  <td>
                    <span className={`badge ${STATUS[m.status][1]}`}>{STATUS[m.status][0]}</span>
                    {m.attempts > 1 && <span className="faint"> · попыток: {m.attempts}</span>}
                    {m.lastError && <div className="field-error">{m.lastError}</div>}
                    {m.status === 'failed' && (
                      <button className="btn btn-ghost" onClick={() => retry.mutate(m.id)}>
                        Повторить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {log.data && <Pager total={log.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>
    </>
  );
}

export function BroadcastPage() {
  const toast = useToast();
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.get<DepartmentDto[]>('/departments') });
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('everyone');
  const [email, setEmail] = useState(false);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent: number }>('/notifications/broadcast', {
        title,
        body: body || undefined,
        email,
        ...(target === 'everyone' ? { everyone: true } : { departmentId: target }),
      }),
    onSuccess: (r) => {
      toast(`Отправлено: ${r.sent}`);
      setTitle('');
      setBody('');
    },
  });
  const err = send.error instanceof ApiError ? send.error : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Рассылка</h1>
          <p className="muted">Уведомление появится у сотрудников в приложении, при желании — и на почте</p>
        </div>
      </div>
      <form className="card card-pad" style={{ display: 'grid', gap: 14, maxWidth: 640 }} onSubmit={submit}>
        {err && <div className="alert">{err.message}</div>}
        <label className="field">
          <span>Кому</span>
          <select className="select" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="everyone">Всем сотрудникам</option>
            {departments.data?.map((d) => (
              <option key={d.id} value={d.id}>
                Отдел: {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Заголовок</span>
          <input className="input" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span>Текст</span>
          <textarea className="textarea" rows={5} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label className="check">
          <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
          <span>Продублировать письмом</span>
        </label>
        <button className="btn btn-primary" disabled={!title.trim() || send.isPending} style={{ justifySelf: 'start' }}>
          Отправить
        </button>
      </form>
    </>
  );
}

type BoolSetting = 'files.notify_approvers_on_new_version' | 'files.notify_initiator_on_new_version';

const SETTING_LABELS: Record<BoolSetting, { title: string; hint: string }> = {
  'files.notify_approvers_on_new_version': {
    title: 'Уведомлять согласовавших о новой версии',
    hint: 'Кто уже согласовал в текущем этапе, узнает о новой версии документа. Согласие при этом не сбрасывается.',
  },
  'files.notify_initiator_on_new_version': {
    title: 'Уведомлять инициатора о новой версии',
    hint: 'Инициатор задачи получит такое же уведомление, когда кто-то загрузит новую версию.',
  },
};

function NumberSetting({ label, value, min, max, onSave }: { label: string; value: number; min: number; max: number; onSave: (v: number) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        max={max}
        defaultValue={value}
        onBlur={(e) => {
          const v = Number(e.target.value);
          if (Number.isInteger(v) && v >= min && v <= max && v !== value) onSave(v);
        }}
      />
    </label>
  );
}

export function SettingsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsDto>('/settings') });
  const save = useMutation({
    mutationFn: (patch: Partial<SettingsDto>) => api.patch<SettingsDto>('/settings', patch),
    onSuccess: (data) => {
      qc.setQueryData(['settings'], data);
      toast('Сохранено');
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Настройки</h1>
          <p className="muted">Меняются сразу, без перезапуска и без разработчика</p>
        </div>
      </div>
      <div className="card card-pad" style={{ display: 'grid', gap: 18, maxWidth: 720 }}>
        <h2>Файлы и версии</h2>
        {settings.data &&
          (Object.keys(SETTING_LABELS) as BoolSetting[]).map((key) => (
            <label key={key} className="check">
              <input type="checkbox" checked={settings.data[key]} disabled={save.isPending} onChange={(e) => save.mutate({ [key]: e.target.checked })} />
              <span>
                <b>{SETTING_LABELS[key].title}</b>
                <br />
                <span className="faint">{SETTING_LABELS[key].hint}</span>
              </span>
            </label>
          ))}
        <h2>Напоминания</h2>
        {settings.data && (
          <>
            <label className="check">
              <input type="checkbox" checked={settings.data['reminders.enabled']} disabled={save.isPending} onChange={(e) => save.mutate({ 'reminders.enabled': e.target.checked })} />
              <span>
                <b>Напоминать о сроках</b>
                <br />
                <span className="faint">В приложении и на почту: скоро срок, срок истёк (и автору — что согласование задерживается), задача ждёт решения без срока, утренняя сводка тем, кто сегодня не заходил</span>
              </span>
            </label>
            <div className="settings-numbers">
              <NumberSetting label="«Скоро срок» — за, часов" min={1} max={240} value={settings.data['reminders.due_soon_hours']} onSave={(v) => save.mutate({ 'reminders.due_soon_hours': v })} />
              <NumberSetting label="Шаг без срока ждёт дольше, часов" min={1} max={720} value={settings.data['reminders.pending_after_hours']} onSave={(v) => save.mutate({ 'reminders.pending_after_hours': v })} />
              <NumberSetting label="Сводка — не раньше, час" min={0} max={23} value={settings.data['reminders.digest_hour']} onSave={(v) => save.mutate({ 'reminders.digest_hour': v })} />
            </div>
          </>
        )}
        {settings.data && <TripSettings s={settings.data} save={(patch) => save.mutate(patch)} />}
      </div>
      {can('guides.manage') && (
        <div className="card card-pad" style={{ display: 'grid', gap: 10, maxWidth: 720, marginTop: 18 }}>
          <h2>Инструкции</h2>
          <p className="muted">
            Чтобы инструкции показывали ваши маршруты, типы заявок, шаблоны и сотрудников, выгрузите настройки и передайте архив тому, кто готовит инструкции. Почт, паролей и документов в архиве нет — только ФИО, должности, отделы и
            настройки.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a className="btn btn-primary" href="/api/guides/export" download>
              Выгрузить настройки для инструкций
            </a>
            <Link className="btn" to="/guides">
              Перейти к инструкциям
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
